import React, { useEffect, useState } from 'react'
import { ITurnoverLog } from '../../types/turnoverLog.type';
import dayjs from 'dayjs';
import { api, createManualTurnoverLog } from '../../api/api';
import { Space, Table, Typography, Button, message, Switch, Tag, Modal, Input, Form } from 'antd';
import { DatePicker } from 'antd';
import { PrinterOutlined, CheckCircleOutlined, ClockCircleOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import './turnoverLogs.page.css';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { TextArea } = Input;

const TurnoverLogs = () => {
    const [logs, setLogs] = useState<ITurnoverLog[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(50);
    const [total, setTotal] = useState<number>(0);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [showCompleted, setShowCompleted] = useState<boolean>(false);
    const [dateRange, setDateRange] = useState<
        [dayjs.Dayjs, dayjs.Dayjs] | undefined
    >([dayjs().startOf("month"), dayjs().endOf("month")]);
    const [searchText, setSearchText] = useState<string>("");
    const [isManualLogModalOpen, setIsManualLogModalOpen] = useState<boolean>(false);
    const [creatingManualLog, setCreatingManualLog] = useState<boolean>(false);
    const [form] = Form.useForm();

    const handleRangeChange = (dates: any) => {
        if (dates) {
            setDateRange([dates[0], dates[1]]);
        } else {
            setDateRange(undefined);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/stats/turnover/logs/`, {
                params: {
                    page,
                    limit: pageSize,
                    startDate: dateRange?.[0]?.format("DD.MM.YYYY"),
                    endDate: dateRange?.[1]?.format("DD.MM.YYYY"),
                    showCompleted: showCompleted.toString(),
                    search: searchText.trim() || undefined,
                }
            });
            setLogs(response.data.logs);
            setTotal(response.data.total);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteLog = async (logId: string) => {
        try {
            await api.post(`/stats/turnover/logs/${logId}/complete`);
            message.success('Запись оборота завершена');
            fetchLogs(); // Обновляем список
        } catch (error) {
            console.error('Error completing log:', error);
            message.error('Ошибка при завершении записи');
        }
    };

    const handleCompleteMultipleLogs = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Выберите записи для завершения');
            return;
        }

        // Фильтруем только незавершенные записи
        const logsToComplete = logs.filter(log => 
            selectedRowKeys.includes(log._id) && log.status === 'pending'
        );

        if (logsToComplete.length === 0) {
            message.warning('Среди выбранных записей нет незавершенных');
            return;
        }

        Modal.confirm({
            title: 'Подтверждение массового завершения',
            content: `Вы уверены, что хотите завершить ${logsToComplete.length} записей?`,
            onOk: async () => {
                try {
                    const logIds = logsToComplete.map(log => log._id);
                    await api.post('/stats/turnover/logs/complete-multiple', { logIds });
                    message.success(`Успешно завершено ${logsToComplete.length} записей`);
                    setSelectedRowKeys([]); // Очищаем выбор
                    fetchLogs(); // Обновляем список
                } catch (error) {
                    console.error('Error completing multiple logs:', error);
                    message.error('Ошибка при массовом завершении записей');
                }
            },
            okText: 'Да',
            cancelText: 'Отмена',
        });
    };

    const confirmComplete = (log: ITurnoverLog) => {
        Modal.confirm({
            title: 'Подтверждение',
            content: `Вы уверены, что хотите завершить запись "${log.description}"?`,
            onOk: () => handleCompleteLog(log._id),
            okText: 'Да',
            cancelText: 'Отмена',
        });
    };

    const handlePrint = () => {
        const selectedLogs = logs.filter(log => selectedRowKeys.includes(log._id));
        const logsToPrint = selectedLogs.length > 0 ? selectedLogs : logs;

        const printContent = `
            <html>
                <head>
                    <title>Отчет оборота</title>
                    <style>
                        * {
                            margin: 0 !important; 
                            padding: 0 !important;
                            box-sizing: border-box;
                        }
                        body {
                            font-family: Arial, sans-serif;
                            font-size: 10px;
                            background: #fff;
                            padding: 0 !important;
                            margin: 0 !important;
                            border: 1px solid #000 !important;
                        }
                        h2 {
                            border: 1px solid #000 !important;
                            margin: 0 !important;
                            padding: 2px 4px !important;
                        }
                        .info-block {
                            border: 1px solid #000 !important;
                            margin: 0 !important;
                            padding: 2px 4px !important;
                        }
                        .print-table {
                            border-collapse: collapse;
                            width: 100%;
                            background: #fff;
                            margin: 0 !important;
                            padding: 0 !important;
                            border: 2px solid #000 !important;
                        }
                        .print-table th,
                        .print-table td {
                            border: 1px solid #000 !important;
                            padding: 2px 4px !important;
                            text-align: left;
                            margin: 0 !important;
                            vertical-align: top;
                        }
                        .print-table th {
                            background-color: #f0f0f0;
                            border: 1px solid #000 !important;
                            font-weight: bold;
                        }
                        .print-table tr {
                            border: 1px solid #000 !important;
                        }
                        .print-table tr:nth-child(even) {
                            background-color: #fafafa;
                        }
                        .print-table thead {
                            border: 1px solid #000 !important;
                        }
                        .print-table tbody {
                            border: 1px solid #000 !important;
                        }
                        .print-table thead tr {
                            border: 2px solid #000 !important;
                        }
                        .print-table tbody tr {
                            border: 1px solid #000 !important;
                        }
                        .deleted-row {
                            background-color: #ffebee !important;
                        }
                        .manual-row {
                            background-color: #fff3cd !important;
                        }

                        @media print {
                            body {
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                                margin: 0 !important;
                                padding: 0 !important;
                                border: 1px solid #000 !important;
                            }
                            h2 {
                                border: 1px solid #000 !important;
                                margin: 0 !important;
                                padding: 2px 4px !important;
                            }
                            .info-block {
                                border: 1px solid #000 !important;
                                margin: 0 !important;
                                padding: 2px 4px !important;
                            }
                            .print-table {
                                border: 2px solid #000 !important;
                            }
                            .print-table th {
                                background-color: #f0f0f0 !important;
                                border: 1px solid #000 !important;
                                font-weight: bold !important;
                            }
                            .print-table td, .print-table th {
                                border: 1px solid #000 !important;
                                margin: 0 !important;
                                padding: 2px 4px !important;
                                vertical-align: top !important;
                            }
                            .print-table tr {
                                border: 1px solid #000 !important;
                            }
                            .print-table thead {
                                border: 1px solid #000 !important;
                            }
                            .print-table tbody {
                                border: 1px solid #000 !important;
                            }
                            .print-table thead tr {
                                border: 2px solid #000 !important;
                            }
                            .print-table tbody tr {
                                border: 1px solid #000 !important;
                            }
                        }
                    </style>
                </head>
                <body>
                    <h2 style="font-size:12px; margin:0; padding:0;">Отчет оборота</h2>
                    <div class="info-block" style="margin:0; padding:0; font-size:11px;"><b>Период:</b> ${dateRange?.[0]?.format("DD.MM.YYYY") || ''} - ${dateRange?.[1]?.format("DD.MM.YYYY") || ''}</div>
                    <div class="info-block" style="margin:0; padding:0; font-size:11px;"><b>Количество записей:</b> ${logsToPrint.length}</div>
                    <table class="print-table" cellspacing="0" cellpadding="0" border="1">
                        <thead>
                            <tr>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">№</th>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">Дата</th>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">Тип</th>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">Оборот до</th>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">Сумма</th>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">Оборот после</th>
                                <th style="border: 1px solid #000 !important; padding: 2px 4px !important;">Описание</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${logsToPrint.map((log, idx) => {
            const description = log.description?.toLowerCase() || '';
            const hasDeleteWords = description.includes('удалено') ||
                description.includes('удаление') ||
                description.includes('удаление');
            const isManual = description.includes('[ручная корректировка]');
            const rowClass = isManual ? 'manual-row' : (hasDeleteWords ? 'deleted-row' : '');

            return `
                                    <tr class="${rowClass}">
                                        <td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.number || (idx + 1)}</td>
                                        <td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.date}</td>
                                        ${log.type ? `<td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.type === 'increase' ? 'Увеличение' : 'Уменьшение'}</td>` : ''}
                                        <td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.turnoverBefore?.toFixed(3) || '0.000'}</td>
                                        <td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.amount?.toFixed(3) || '0.000'}</td>
                                        <td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.turnoverAfter?.toFixed(3) || '0.000'}</td>
                                        <td style="border: 1px solid #000 !important; padding: 2px 4px !important;">${log.description}</td>
                                    </tr>
                                `;
        }).join('')}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
        }
    };

    const handleCreateManualLog = async () => {
        try {
            const values = await form.validateFields();
            setCreatingManualLog(true);
            await createManualTurnoverLog(values.description);
            message.success('Ручной лог успешно создан');
            form.resetFields();
            setIsManualLogModalOpen(false);
            fetchLogs();
        } catch (error: any) {
            if (error.errorFields) {
                // Validation error
                return;
            }
            console.error('Error creating manual log:', error);
            message.error('Ошибка при создании ручного лога');
        } finally {
            setCreatingManualLog(false);
        }
    };

    const handleSearch = () => {
        setPage(1); // Сбрасываем на первую страницу при поиске
        fetchLogs();
    };

    useEffect(() => {
        fetchLogs();
        // Очищаем выбранные записи при изменении фильтров
        setSelectedRowKeys([]);
    }, [page, pageSize, dateRange, showCompleted]);

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
        // Разрешаем выбор только незавершенных записей
        getCheckboxProps: (record: ITurnoverLog) => ({
            disabled: record.status === 'completed',
        }),
    };

    const columns = [
        {
            title: "№",
            dataIndex: "number",
            key: "number",
            width: 50,
            render: (number: number, record: ITurnoverLog, index: number) => {
                return number || ((page - 1) * pageSize + index + 1);
            }
        },
        {
            title: "Дата",
            dataIndex: "date",
            key: "date",
            width: 100,
        },
        {
            title: "Статус",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status: 'pending' | 'completed') => {
                if (status === 'completed') {
                    return <Tag color="green" icon={<CheckCircleOutlined />}>Завершено</Tag>;
                } else {
                    return <Tag color="orange" icon={<ClockCircleOutlined />}>В ожидании</Tag>;
                }
            },
        },
        {
            title: "Тип",
            dataIndex: "type",
            key: "type",
            width: 100,
            render: (type: 'increase' | 'decrease' | undefined) => {
                if (!type) return null;
                return (
                    <span style={{
                        color: type === 'increase' ? '#52c41a' : '#ff4d4f',
                        fontWeight: 'bold'
                    }}>
                        {type === 'increase' ? 'Увеличение' : 'Уменьшение'}
                    </span>
                );
            },
        },
        {
            title: "Оборот до",
            dataIndex: "turnoverBefore",
            key: "turnoverBefore",
            width: 100,
            render: (value: number) => value?.toFixed(3) || '0.000',
        },
        {
            title: "Оборот после",
            dataIndex: "turnoverAfter",
            key: "turnoverAfter",
            width: 100,
            render: (value: number) => value?.toFixed(3) || '0.000',
        },
        {
            title: "Описание",
            dataIndex: "description",
            key: "description",
            width: 200,
        },
        {
            title: "Сумма",
            dataIndex: "amount",
            key: "amount",
            width: 100,
            render: (value: number) => value?.toFixed(3) || '0.000',
        },
        {
            title: "Действия",
            key: "actions",
            width: 120,
            render: (_: any, record: ITurnoverLog) => {
                if (record.status === 'completed') {
                    return <span style={{ color: '#999' }}>Завершено</span>;
                }
                return (
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => confirmComplete(record)}
                        icon={<CheckCircleOutlined />}
                    >
                        Завершить
                    </Button>
                );
            },
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            <Title level={4} style={{ fontSize: '14px', margin: '0 0 8px 0' }}>Отчеты оборота</Title>
            <Space style={{ marginBottom: 8 }} wrap>
                <RangePicker
                    onChange={handleRangeChange}
                    placeholder={["Начало", "Конец"]}
                    format="DD.MM.YYYY"
                    value={dateRange}
                    size="small"
                />
                <Input
                    placeholder="Поиск по описанию..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ width: 200 }}
                    size="small"
                    allowClear
                />
                <Button
                    type="default"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    size="small"
                >
                    Поиск
                </Button>
                <Switch
                    checked={showCompleted}
                    onChange={setShowCompleted}
                    checkedChildren="Показать завершенные"
                    unCheckedChildren="Скрыть завершенные"
                    size="small"
                />
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsManualLogModalOpen(true)}
                    size="small"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                    Создать ручной лог
                </Button>
                <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleCompleteMultipleLogs}
                    size="small"
                    disabled={selectedRowKeys.length === 0}
                >
                    Завершить выбранные ({selectedRowKeys.length})
                </Button>
                <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={handlePrint}
                    size="small"
                >
                    Печать {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : '(все)'}
                </Button>
            </Space>
            <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={logs}
                rowKey={(record) => record._id}
                pagination={{
                    current: page,
                    total: total,
                    pageSize: pageSize,
                    onChange: (page, size) => {
                        setPage(page);
                        if (size !== pageSize) setPageSize(size!);
                    },
                    showSizeChanger: true,
                    size: "small",
                }}
                scroll={{ x: 800 }}
                size="small"
                bordered
                style={{ fontSize: '10px' }}
                rowClassName={(record) => {
                    const description = record.description?.toLowerCase() || '';
                    const hasDeleteWords = description.includes('удалено') ||
                        description.includes('удаление') ||
                        description.includes('удаление');
                    const isManual = description.includes('[ручная корректировка]');
                    return isManual ? 'manual-row' : (hasDeleteWords ? 'deleted-row' : '');
                }}
            />

            {/* Модальное окно для создания ручного лога */}
            <Modal
                title="Создать ручной лог оборота"
                open={isManualLogModalOpen}
                onOk={handleCreateManualLog}
                onCancel={() => {
                    setIsManualLogModalOpen(false);
                    form.resetFields();
                }}
                okText="Создать"
                cancelText="Отмена"
                width={500}
                confirmLoading={creatingManualLog}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        label="Описание"
                        name="description"
                        rules={[
                            { required: true, message: 'Введите описание' },
                            { min: 5, message: 'Описание должно содержать минимум 5 символов' }
                        ]}
                        extra="Сумма будет рассчитана автоматически как разница между текущим и предыдущим оборотом"
                    >
                        <TextArea
                            rows={4}
                            placeholder="Введите подробное описание причины ручной корректировки..."
                            maxLength={500}
                            showCount
                            disabled={creatingManualLog}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default TurnoverLogs